/**
 * Progress-report aggregation (ADR-0055). Pure functions over the local
 * activity-log / body-metric / program rows — the dashboard computes
 * everything client-side (identical for guest/free/Pro, works offline).
 *
 * Honesty rule: only logs with `endedAt` (completion capture) count as
 * workouts; legacy start-only rows are ignored by every aggregate.
 */

import { ActivityLog, WorkoutProgram } from '../models/sequal.models';
import { STREAK_REST_DAY_ALLOWANCE } from '../../../../common/models/progress.models';

const DAY_MS = 86_400_000;

/** A completed session (has an honest end time). */
export function isCompleted(log: ActivityLog): boolean {
  return log.endedAt != null;
}

/** Local-midnight epoch ms for the day containing `ms`. */
export function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Local-midnight epoch ms of the MONDAY starting the week containing `ms`. */
export function startOfWeek(ms: number): number {
  const d = new Date(startOfDay(ms));
  const shift = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  return d.getTime() - shift * DAY_MS;
}

export interface RangeSummary {
  workouts: number;
  minutes: number;
  reps: number;
  calories: number;
}

/** Totals over completed sessions with `from <= startedAt < to`. */
export function summarizeRange(logs: ActivityLog[], from: number, to: number): RangeSummary {
  const out: RangeSummary = { workouts: 0, minutes: 0, reps: 0, calories: 0 };
  for (const l of logs) {
    if (!isCompleted(l) || l.startedAt < from || l.startedAt >= to) continue;
    out.workouts += 1;
    out.minutes += (l.durationSec ?? 0) / 60;
    out.reps += l.reps ?? 0;
    out.calories += l.calories ?? 0;
  }
  out.minutes = Math.round(out.minutes);
  out.calories = Math.round(out.calories);
  return out;
}

/** One bucket of a time-series chart. */
export interface TimeBucket extends RangeSummary {
  /** Bucket start (local midnight / week Monday / 1st of month), epoch ms. */
  start: number;
}

/** Per-day buckets for the last `days` days (oldest first, today last). */
export function dailyBuckets(logs: ActivityLog[], days: number, now = Date.now()): TimeBucket[] {
  const out: TimeBucket[] = [];
  const today = startOfDay(now);
  for (let i = days - 1; i >= 0; i--) {
    const start = today - i * DAY_MS;
    out.push({ start, ...summarizeRange(logs, start, start + DAY_MS) });
  }
  return out;
}

/** Per-week buckets (Mon-start) for the last `weeks` weeks (oldest first). */
export function weeklyBuckets(logs: ActivityLog[], weeks: number, now = Date.now()): TimeBucket[] {
  const out: TimeBucket[] = [];
  const thisWeek = startOfWeek(now);
  for (let i = weeks - 1; i >= 0; i--) {
    const start = thisWeek - i * 7 * DAY_MS;
    out.push({ start, ...summarizeRange(logs, start, start + 7 * DAY_MS) });
  }
  return out;
}

/** Per-calendar-month buckets for the last `months` months (oldest first). */
export function monthlyBuckets(logs: ActivityLog[], months: number, now = Date.now()): TimeBucket[] {
  const out: TimeBucket[] = [];
  const ref = new Date(now);
  for (let i = months - 1; i >= 0; i--) {
    const start = new Date(ref.getFullYear(), ref.getMonth() - i, 1).getTime();
    const end = new Date(ref.getFullYear(), ref.getMonth() - i + 1, 1).getTime();
    out.push({ start, ...summarizeRange(logs, start, end) });
  }
  return out;
}

export interface StreakInfo {
  /** Consecutive workout-day streak ending today/yesterday (rest-day tolerant). */
  current: number;
  longest: number;
}

/**
 * Workout-day streaks over completed sessions. Gaps of up to
 * {@link STREAK_REST_DAY_ALLOWANCE} rest day(s) between workout days do NOT
 * break a streak (encourage, don't punish); only workout days are counted.
 * The current streak survives while today is within the allowance window of
 * the last workout day.
 */
export function streaks(
  logs: ActivityLog[],
  now = Date.now(),
  restAllowance = STREAK_REST_DAY_ALLOWANCE,
): StreakInfo {
  const days = [...new Set(logs.filter(isCompleted).map((l) => startOfDay(l.startedAt)))].sort(
    (a, b) => a - b,
  );
  if (!days.length) return { current: 0, longest: 0 };
  const maxGap = (restAllowance + 1) * DAY_MS;
  let longest = 1;
  let run = 1;
  for (let i = 1; i < days.length; i++) {
    if (days[i] - days[i - 1] <= maxGap) run += 1;
    else run = 1;
    if (run > longest) longest = run;
  }
  const today = startOfDay(now);
  const lastDay = days[days.length - 1];
  const current = today - lastDay <= maxGap ? run : 0;
  return { current, longest };
}

/** Day-level membership of the current streak (activity-calendar chain). */
export interface StreakDays {
  /** Day-starts (local midnight ms) with a completed workout in the current streak. */
  workoutDays: Set<number>;
  /** Rest day-starts BETWEEN workout days of the current streak — tolerated by
   *  the allowance, shown as "protected" in the calendar. */
  restDays: Set<number>;
}

/**
 * Which calendar days belong to the CURRENT streak, under the same rules as
 * {@link streaks}: the run of workout days ending within the allowance window
 * of today, plus the tolerated rest days between them. Both sets are empty
 * when no streak is alive.
 */
export function currentStreakDays(
  logs: ActivityLog[],
  now = Date.now(),
  restAllowance = STREAK_REST_DAY_ALLOWANCE,
): StreakDays {
  const out: StreakDays = { workoutDays: new Set(), restDays: new Set() };
  const days = [...new Set(logs.filter(isCompleted).map((l) => startOfDay(l.startedAt)))].sort(
    (a, b) => a - b,
  );
  if (!days.length) return out;
  const maxGap = (restAllowance + 1) * DAY_MS;
  if (startOfDay(now) - days[days.length - 1] > maxGap) return out;
  let from = days.length - 1;
  while (from > 0 && days[from] - days[from - 1] <= maxGap) from--;
  for (let i = from; i < days.length; i++) {
    out.workoutDays.add(days[i]);
    if (i === from) continue;
    for (let d = days[i - 1] + DAY_MS; d < days[i]; d += DAY_MS) out.restDays.add(d);
  }
  return out;
}

// ── Plan (program) progress & schedule ───────────────────────────────────────

export enum PlanScheduleStatus {
  /** No startDate set — the plan is browsed sequentially. */
  UNSCHEDULED = 'unscheduled',
  /** startDate is in the future. */
  UPCOMING = 'upcoming',
  ON_TRACK = 'on-track',
  BEHIND = 'behind',
  COMPLETED = 'completed',
}

export interface PlanProgress {
  programId: string;
  name: string;
  totalDays: number;
  /** Distinct 1-based days with a completed session log. */
  completedDays: number[];
  /** First not-yet-completed day (the "next up" link), or null when done. */
  nextDay: number | null;
  status: PlanScheduleStatus;
  /** UPCOMING: whole days until the plan starts. */
  startsInDays?: number;
  /** Scheduled plans: the 1-based day due today (clamped to totalDays). */
  dueDay?: number;
  /** BEHIND: how many due days are still incomplete (soft, never guilt-based). */
  behindByDays?: number;
}

/** Progress for every live (non-deleted, non-draft) program. */
export function planProgress(
  programs: WorkoutProgram[],
  logs: ActivityLog[],
  now = Date.now(),
): PlanProgress[] {
  const completedByProgram = new Map<string, Set<number>>();
  for (const l of logs) {
    if (!isCompleted(l) || !l.programId || !l.programDay) continue;
    let set = completedByProgram.get(l.programId);
    if (!set) completedByProgram.set(l.programId, (set = new Set()));
    set.add(l.programDay);
  }
  return programs
    .filter((p) => !p.deletedAt && !p.isDraft && p.totalDays > 0)
    .map((p) => {
      const done = [...(completedByProgram.get(p.id) ?? [])]
        .filter((d) => d >= 1 && d <= p.totalDays)
        .sort((a, b) => a - b);
      const doneSet = new Set(done);
      let nextDay: number | null = null;
      for (let d = 1; d <= p.totalDays; d++) {
        if (!doneSet.has(d)) { nextDay = d; break; }
      }
      const out: PlanProgress = {
        programId: p.id,
        name: p.name,
        totalDays: p.totalDays,
        completedDays: done,
        nextDay,
        status: PlanScheduleStatus.UNSCHEDULED,
      };
      if (nextDay === null) {
        out.status = PlanScheduleStatus.COMPLETED;
        return out;
      }
      if (p.startDate == null) return out;
      const today = startOfDay(now);
      const start = startOfDay(p.startDate);
      if (start > today) {
        out.status = PlanScheduleStatus.UPCOMING;
        out.startsInDays = Math.round((start - today) / DAY_MS);
        return out;
      }
      const dueDay = Math.min(Math.floor((today - start) / DAY_MS) + 1, p.totalDays);
      out.dueDay = dueDay;
      // Behind = due days not yet completed, minus today's (still doable today).
      const missed = [];
      for (let d = 1; d < dueDay; d++) if (!doneSet.has(d)) missed.push(d);
      out.behindByDays = missed.length;
      out.status = missed.length > 0 ? PlanScheduleStatus.BEHIND : PlanScheduleStatus.ON_TRACK;
      return out;
    });
}

/** A plan day projected onto a calendar date (activity-calendar overlay). */
export interface ScheduledDay {
  programId: string;
  name: string;
  /** 1-based program day due on this date. */
  programDay: number;
  /** The date is in the past and the day was never completed. */
  missed: boolean;
}

/**
 * Projects scheduled (startDate-anchored) plans onto calendar dates within
 * [from, to) — one program day per calendar day from the start, mirroring the
 * dueDay rule in {@link planProgress}. Completed days project nothing; past
 * dates therefore only appear when the due day was missed (today is still
 * doable and never "missed"). Unscheduled plans project nothing.
 */
export function scheduleByDay(
  programs: WorkoutProgram[],
  logs: ActivityLog[],
  from: number,
  to: number,
  now = Date.now(),
): Map<number, ScheduledDay[]> {
  const doneByProgram = new Map<string, Set<number>>();
  for (const l of logs) {
    if (!isCompleted(l) || !l.programId || !l.programDay) continue;
    let set = doneByProgram.get(l.programId);
    if (!set) doneByProgram.set(l.programId, (set = new Set()));
    set.add(l.programDay);
  }
  const out = new Map<number, ScheduledDay[]>();
  const today = startOfDay(now);
  for (const p of programs) {
    if (p.deletedAt || p.isDraft || !p.totalDays || p.startDate == null) continue;
    const done = doneByProgram.get(p.id) ?? new Set<number>();
    const start = startOfDay(p.startDate);
    for (let day = 1; day <= p.totalDays; day++) {
      const date = start + (day - 1) * DAY_MS;
      if (date < from || date >= to || done.has(day)) continue;
      const entry: ScheduledDay = {
        programId: p.id,
        name: p.name,
        programDay: day,
        missed: date < today,
      };
      const list = out.get(date);
      if (list) list.push(entry);
      else out.set(date, [entry]);
    }
  }
  return out;
}

// ── Category distribution (Pro trends donut) ─────────────────────────────────

/** Bucket slug for sessions whose item has no known category. */
export const OTHER_CATEGORY = 'other';

/**
 * Minutes of completed training per category slug. `categoriesOf` resolves a
 * log to its played item's category ids (activity/group/sequence join —
 * supplied by the caller, which owns the item maps). Uncategorised time lands
 * in {@link OTHER_CATEGORY}. Sorted by minutes, descending.
 */
export function categoryMinutes(
  logs: ActivityLog[],
  categoriesOf: (log: ActivityLog) => string[] | undefined,
): Array<{ slug: string; minutes: number }> {
  const bySlug = new Map<string, number>();
  for (const l of logs) {
    if (!isCompleted(l)) continue;
    const minutes = (l.durationSec ?? 0) / 60;
    if (minutes <= 0) continue;
    const slug = categoriesOf(l)?.[0] ?? OTHER_CATEGORY;
    bySlug.set(slug, (bySlug.get(slug) ?? 0) + minutes);
  }
  return [...bySlug.entries()]
    .map(([slug, minutes]) => ({ slug, minutes: Math.round(minutes) }))
    .filter((e) => e.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
}

/**
 * Minutes per category PER DAY (activity-calendar tint + legend). Same
 * resolution rule as {@link categoryMinutes}. Each day's entries are sorted by
 * minutes descending, so `[0]` is the day's dominant category.
 */
export function dailyCategoryMinutes(
  logs: ActivityLog[],
  categoriesOf: (log: ActivityLog) => string[] | undefined,
): Map<number, Array<{ slug: string; minutes: number }>> {
  const byDay = new Map<number, Map<string, number>>();
  for (const l of logs) {
    if (!isCompleted(l)) continue;
    const minutes = (l.durationSec ?? 0) / 60;
    if (minutes <= 0) continue;
    const day = startOfDay(l.startedAt);
    const slug = categoriesOf(l)?.[0] ?? OTHER_CATEGORY;
    let m = byDay.get(day);
    if (!m) byDay.set(day, (m = new Map()));
    m.set(slug, (m.get(slug) ?? 0) + minutes);
  }
  const out = new Map<number, Array<{ slug: string; minutes: number }>>();
  for (const [day, m] of byDay) {
    out.set(
      day,
      [...m.entries()]
        .map(([slug, minutes]) => ({ slug, minutes }))
        .sort((a, b) => b.minutes - a.minutes),
    );
  }
  return out;
}

// ── Personal records & milestones ────────────────────────────────────────────

export interface PersonalRecords {
  /** Longest completed session (seconds) + when it was set. */
  longestSessionSec: number;
  longestSessionAt?: number;
  /** Most reps completed in one calendar day + the day. */
  mostRepsInDay: number;
  mostRepsInDayAt?: number;
  /** Most workouts in one Mon-start week + the week start. */
  bestWeekWorkouts: number;
  bestWeekStart?: number;
  totalWorkouts: number;
  totalMinutes: number;
  totalReps: number;
}

export function personalRecords(logs: ActivityLog[]): PersonalRecords {
  const done = logs.filter(isCompleted);
  const out: PersonalRecords = {
    longestSessionSec: 0,
    mostRepsInDay: 0,
    bestWeekWorkouts: 0,
    totalWorkouts: done.length,
    totalMinutes: 0,
    totalReps: 0,
  };
  const repsByDay = new Map<number, number>();
  const workoutsByWeek = new Map<number, number>();
  for (const l of done) {
    const dur = l.durationSec ?? 0;
    if (dur > out.longestSessionSec) {
      out.longestSessionSec = dur;
      out.longestSessionAt = l.startedAt;
    }
    out.totalMinutes += dur / 60;
    out.totalReps += l.reps ?? 0;
    const day = startOfDay(l.startedAt);
    repsByDay.set(day, (repsByDay.get(day) ?? 0) + (l.reps ?? 0));
    const week = startOfWeek(l.startedAt);
    workoutsByWeek.set(week, (workoutsByWeek.get(week) ?? 0) + 1);
  }
  for (const [day, reps] of repsByDay) {
    if (reps > out.mostRepsInDay) { out.mostRepsInDay = reps; out.mostRepsInDayAt = day; }
  }
  for (const [week, n] of workoutsByWeek) {
    if (n > out.bestWeekWorkouts) { out.bestWeekWorkouts = n; out.bestWeekStart = week; }
  }
  out.totalMinutes = Math.round(out.totalMinutes);
  return out;
}
